import React from 'react';

import { motion } from 'framer-motion';

import type { MegaNavItem, MenuLink } from '../../types';
import { MenuLinkItem } from '../components/MenuLinkItem';
import { MobileMegaNavButton } from './MobileMegaNavButton';

interface MobileMenuListProps {
  megaNavData?: MegaNavItem[];
  menuPanelNavLinks: MenuLink[];
  mainNavLinks: MenuLink[];
  slug: string;
  tabletDisplay: string;
  onSubMenuOpen: (itemName: string) => void;
}

const mainMenuVariants = {
  hidden: {
    x: '-100%',
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
  visible: {
    x: 0,
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
};

export const MobileMenuList: React.FC<MobileMenuListProps> = ({
  megaNavData,
  menuPanelNavLinks,
  mainNavLinks,
  slug,
  tabletDisplay,
  onSubMenuOpen,
}) => {
  if (megaNavData) {
    return (
      <motion.div
        initial="visible"
        animate="visible"
        exit="hidden"
        variants={mainMenuVariants}
        style={{ overflowY: 'auto', overflowX: 'hidden' }}
      >
        {megaNavData.map((item, idx) => {
          const hasSections = item.sections && item.sections.length > 0;

          if (hasSections) {
            return (
              <MobileMegaNavButton
                key={`mega-${idx}`}
                itemName={item.name}
                onClick={() => onSubMenuOpen(item.name)}
              />
            );
          }

          // For items without sections (like Agents), render as a simple link
          return (
            <MenuLinkItem
              key={`mega-${idx}`}
              href={item.href}
              name={item.name}
              analyticsName={`cosmos-mega-nav-mobile-${item.name.toLowerCase()}`}
              isActive={slug === item.href}
              showOnTablet={true}
              tabletDisplay="flex"
            />
          );
        })}
        {menuPanelNavLinks?.map((link, idx) => (
          <MenuLinkItem
            key={`panel-${idx}`}
            href={link.href}
            name={link.name}
            analyticsName={`cosmos-landing-page-v2-menu-link-${link.name}`}
            isActive={slug === link.href}
            showOnTablet={true}
            tabletDisplay="flex"
          />
        ))}
      </motion.div>
    );
  }

  return (
    <>
      {mainNavLinks.map((nav, idx) => (
        <MenuLinkItem
          key={`main-${idx}`}
          href={nav.href}
          name={nav.name}
          analyticsName={`cosmos-landing-page-v2-nav-link-${nav.name}`}
          isActive={slug === nav.href}
          showOnTablet={true}
          tabletDisplay={tabletDisplay}
        />
      ))}
      {menuPanelNavLinks?.map((link, idx) => (
        <MenuLinkItem
          key={`panel-${idx}`}
          href={link.href}
          name={link.name}
          analyticsName={`cosmos-landing-page-v2-menu-link-${link.name}`}
          isActive={slug === link.href}
        />
      ))}
    </>
  );
};
